import React, { useEffect, useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import Axios from 'axios';
import parse from 'html-react-parser';

import { BackDropLoad } from './LoadingBackDrop';
import { Loading } from './Loading';
import { UserContext, SnackbarContext } from './userContext';
import {
  AccountHeader,
  SectionHead,
  FormCard,
  SelectField,
  PrimaryButton,
  DetailRow,
  Icon,
} from './myacc/primitives';

function loadScript(src) {
  return new Promise((resolve) => {
    const script = document.createElement('script');
    script.src = src;
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}

function planLabel(months) {
  const m = parseInt(months, 10);
  if (m < 12) return `${m} Months`;
  if (m === 12) return '1 Year';
  return `${m / 12} Years`;
}

export const MyAccSubscription = () => {
  const history = useNavigate();

  const [error, setError] = useState(0);
  const [loading, setLoading] = useState(true);
  const [wait, setWait] = useState(false);
  const [subStatus, setSubStatus] = useState(false);
  const [subDate, setSubDate] = useState('');
  const [data, setData] = useState();
  const [subOff, setSubOff] = useState('');

  const [price, setPrice] = useState('0');
  const [packType, setPackType] = useState('0');
  const [packMonth, setPackMonth] = useState('0');
  const [packMonthsText, setPackMonthsText] = useState('0');

  const [orderID, setOrderID] = useState('');
  const [, setRzpId] = useState('');

  const { user, setUser } = useContext(UserContext);
  const { setSnack } = useContext(SnackbarContext);

  const options = {
    key: 'rzp_live_6FPbncuP1okNQk',
    currency: 'INR',
    amount: String(((parseInt(price, 10) * 18) / 100 + parseInt(price, 10)) * 100),
    order_id: orderID,
    name: 'ESAY Subscription',
    description: `${packMonthsText} Subscription Plan.`,
    payment_capture: '0',
    handler(response) {
      if (typeof response.razorpay_payment_id === 'undefined' || response.razorpay_payment_id < 1) {
        setSnack({ message: 'Payment failed. Please try again.', type: 'error', open: true, direction: 'center' });
      } else {
        setRzpId(response.razorpay_payment_id);
        createSubscription(response.razorpay_payment_id);
      }
    },
    prefill: { name: user.name, email: user.user_email, contact: user.user_mobile },
  };

  useEffect(() => { getData(); }, []); // eslint-disable-line

  const getData = () => {
    const formData = { role: user.user_role, cat: user.category_ids, uid: user.user_id };
    Axios.post('/new_apis/getsubpackages', formData, { headers: { tkn: user.token } }).then((res) => {
      if (res.data.status === 1) {
        setData(res.data.data);
        setSubStatus(res.data.substatus);
        setSubDate(res.data.expdate);
        setSubOff(res.data.suboff[0].off_desc);
        setLoading(false);
      } else if (res.data.status === 3) {
        history('/');
        setSnack({ message: res.data.msg, type: 'error', open: true, direction: 'center' });
      } else if (res.data.status === 4) {
        setUser(null);
        localStorage.removeItem('estkn');
        history('/');
        setSnack({ message: res.data.msg, type: 'error', open: true, direction: 'center' });
      }
    });
  };

  const subscribeNow = async (e) => {
    e.preventDefault();
    setError(0);
    if (price === '0') {
      setError(1);
      setSnack({ message: 'Please choose a subscription plan.', type: 'error', open: true, direction: 'center' });
      return;
    }
    setWait(true);
    const formData = {
      role: user.user_role,
      cat: user.category_ids,
      uid: user.user_id,
      price: String(((parseInt(price, 10) * 18) / 100 + parseInt(price, 10)) * 100),
    };
    await Axios.post('/new_apis/createorderid', formData, { headers: { tkn: user.token } }).then((res) => {
      if (res.data.status === 1) {
        setOrderID(res.data.orderid);
        setWait(false);
        openRazorpay();
      } else if (res.data.status === 3) {
        history('/');
        setSnack({ message: res.data.msg, type: 'error', open: true, direction: 'center' });
      } else if (res.data.status === 4) {
        setUser(null);
        localStorage.removeItem('estkn');
        history('/');
        setSnack({ message: res.data.msg, type: 'error', open: true, direction: 'center' });
      }
    });
  };

  const createSubscription = async (paymentid) => {
    setLoading(true);
    const formData = {
      role: user.user_role,
      cat: user.category_ids,
      uid: user.user_id,
      packmonths: packMonth,
      price,
      rzpid: paymentid,
      packtype: packType,
    };
    await Axios.post('/new_apis/createsubscription', formData, { headers: { tkn: user.token } }).then((res) => {
      if (res.data.status === 1) {
        getData();
      } else if (res.data.status === 3) {
        history('/');
        setSnack({ message: res.data.msg, type: 'error', open: true, direction: 'center' });
      } else if (res.data.status === 4) {
        setUser(null);
        localStorage.removeItem('estkn');
        history('/');
        setSnack({ message: res.data.msg, type: 'error', open: true, direction: 'center' });
      }
    });
  };

  const openRazorpay = async () => {
    await loadScript('https://checkout.razorpay.com/v1/checkout.js');
    const paymentObject = new window.Razorpay(options);
    paymentObject.open();
  };

  if (loading) return <Loading />;

  const subTotal = parseInt(price, 10) || 0;
  const gst = (subTotal * 18) / 100;
  const total = subTotal + gst;

  return (
    <>
      <BackDropLoad wait={wait} />
      <AccountHeader
        kicker="Account"
        title="My subscription"
        subtitle="Review your plan and renew or upgrade when needed."
      />

      {/* Status card */}
      <section className="relative overflow-hidden rounded-2xl border border-border-subtle bg-surface-paper p-5 mb-4">
        <div
          aria-hidden
          className="pointer-events-none absolute -top-16 -right-16 h-44 w-44 rounded-full"
          style={{ background: subStatus ? 'radial-gradient(circle, rgba(251,191,36,0.14), transparent 70%)' : 'radial-gradient(circle, rgba(251,191,36,0.06), transparent 70%)' }}
        />
        <div className="relative flex flex-col sm:flex-row sm:items-center gap-3">
          <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl ${subStatus ? 'bg-amber/15 text-amber' : 'bg-surface-overlay/60 text-fg-tertiary'}`}>
            <Icon.Card className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-fg-tertiary mb-0.5">
              Current plan
            </div>
            <h2 className="text-[16px] font-bold text-fg-primary truncate">{data?.name || '-'}</h2>
            <div className="mt-0.5 inline-flex items-center gap-1.5">
              {subStatus ? (
                <>
                  <span aria-hidden className="inline-flex h-1.5 w-1.5 rounded-full bg-amber animate-pulse" />
                  <span className="text-[11.5px] font-semibold text-amber">Active until {subDate}</span>
                </>
              ) : (
                <>
                  <span aria-hidden className="inline-flex h-1.5 w-1.5 rounded-full bg-fg-muted" />
                  <span className="text-[11.5px] font-semibold text-fg-tertiary">Not subscribed</span>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {!subStatus && data && (
        <>
          <SectionHead kicker="Subscribe" title="Choose a plan" />
          <FormCard onSubmit={subscribeNow}>
            <div className="grid grid-cols-1 gap-4">
              <SelectField
                label="Subscription plan"
                value={price === '0' ? '0' : packType}
                onChange={(e) => {
                  setOrderID('');
                  const v = e.target.value;
                  if (v === '0') {
                    setPrice('0'); setPackType('0'); setPackMonth('0'); setPackMonthsText('0');
                    return;
                  }
                  const item = data.ptypes.find((p) => p.package_type_price === v);
                  if (item) {
                    setPrice(item.package_type_price);
                    setPackType(item.package_type_price);
                    setPackMonth(item.package_type_months);
                    setPackMonthsText(planLabel(item.package_type_months));
                  }
                }}
                error={error === 1}
                errorMsg="Please choose a plan."
                required
              >
                <option value="0">Choose a plan</option>
                {data.ptypes.map((da) => (
                  <option key={da.package_type_price} value={da.package_type_price}>
                    {planLabel(da.package_type_months)} - Rs. {da.package_type_price}
                  </option>
                ))}
              </SelectField>

              {price !== '0' && (
                <div className="rounded-xl border border-border-subtle bg-surface-overlay/30 p-4">
                  <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-amber mb-2">
                    Order summary
                  </div>
                  <DetailRow label="Sub total" value={`Rs. ${subTotal}`} />
                  <DetailRow label="GST (18%)" value={`Rs. ${gst}`} />
                  <DetailRow
                    label="Total"
                    value={<span className="text-amber font-bold text-[15px]">Rs. {total}</span>}
                  />
                </div>
              )}

              <div className="pt-1">
                <PrimaryButton type="submit" loading={wait} icon={<Icon.ArrowRight className="h-4 w-4" />}>
                  {wait ? 'Please wait…' : 'Subscribe now'}
                </PrimaryButton>
              </div>
            </div>
          </FormCard>
        </>
      )}

      {subOff && (
        <section className="relative mt-4 rounded-2xl border border-amber/24 bg-amber/5 p-5 flex flex-col sm:flex-row sm:items-start gap-3">
          <span aria-hidden className="absolute left-0 top-3 bottom-3 w-[3px] rounded-r-full bg-amber" />
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-amber/15 text-amber">
            <Icon.Info className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="text-[14px] font-bold text-fg-primary mb-1">Offer details</h3>
            <div className="text-[12.5px] leading-[1.6] text-fg-secondary [&_*]:text-fg-secondary">
              {parse(subOff)}
            </div>
          </div>
        </section>
      )}
    </>
  );
};
